import classNames from "classnames";
import { isFunction } from "lodash-es";
import React, { memo, useCallback } from "react";
import ReactSelect from "react-select";
import Creatable from "react-select/creatable";

import { testAttr } from "@/browser";

import { Icon } from "../../../Icon";
import { ISelectProps, SelectComponents, SELECT_CLASS_NAME } from "./components";

const {
  ClearIndicator,
  DropdownIndicator,
  Input,
  LoadingIndicator,
  AutoPositionedMenu,
  MenuList,
  MultiValue,
  MultiValueRemove,
  Option,
  SingleValue,
} = SelectComponents;

export const Select = memo(<T,>(props: ISelectProps<T>) => {
  const {
    className,
    components,
    hideSelectedOptions = true,
    icons = {
      clear: <Icon icon="close" size="sm" />,
      dropdownMenuOpen: <Icon icon="arrow_up_2" size="sm" />,
      dropdownMenuClosed: <Icon icon="arrow_down_2" size="sm" />,
    }, // not used by react-select
    invalid,
    isDisabled,
    labelKey = "value", // not used by react-select
    onChange,
    placeholder = "Select...",
    readonly,
    size = "md", // not used by react-select
    valueKey = "code", // not used by react-select,
    testName, // not user by react-select
    isCreatable = false,
    ...rest
  } = props;

  const getOptionLabel = useCallback(
    (option: any) => {
      if (isFunction(labelKey)) {
        return labelKey(option);
      }
      return option[labelKey as string];
    },
    [labelKey],
  );

  const getOptionValue = useCallback(
    (option: any) => {
      return option[valueKey as string];
    },
    [valueKey],
  );

  const handleChange = useCallback(
    (value: any | null, _actionMeta: any) => {
      if (onChange) {
        onChange(value);
      }
    },
    [onChange],
  );

  const wrapperClasses = classNames(className, `${SELECT_CLASS_NAME}-wrapper`);
  const classes = classNames(SELECT_CLASS_NAME, {
    [`${SELECT_CLASS_NAME}--size-${size}`]: !!size,
    [`${SELECT_CLASS_NAME}--not-valid`]: invalid,
  });

  const SelectComponent: any = isCreatable ? Creatable : ReactSelect;

  return (
    <span className={wrapperClasses} {...testAttr({ testName }, { sufix: "select" })}>
      <SelectComponent
        className={classes}
        classNamePrefix={SELECT_CLASS_NAME}
        components={{
          ClearIndicator,
          DropdownIndicator,
          IndicatorSeparator: () => null,
          Input,
          LoadingIndicator,
          Menu: AutoPositionedMenu,
          MenuList,
          MultiValue,
          MultiValueRemove,
          Option,
          SingleValue,
          ...components,
        }}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        hideSelectedOptions={hideSelectedOptions}
        icons={icons}
        isDisabled={isDisabled || readonly}
        onChange={handleChange}
        placeholder={placeholder}
        size={size}
        invalid={invalid}
        {...rest}
      />
    </span>
  );
});

Select.displayName = "Select";
